import { apiClient } from "../../lib/http/apiClient";
import { adminStorage } from "../admin/service";
import type {
  ApplyProgress,
  ApplyStartResult,
  AudiencePreview,
  Campaign,
  CampaignPayload,
  Coupon,
  CouponPayload,
  CouponRedemption,
  RecipientsFilter,
  RecipientsPage,
  RecipientsSummary,
  ResolveUsersResult,
  WelcomeBonusConfig,
  WelcomeBonusState,
} from "./types";

function adminHeaders() {
  const token = adminStorage.getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

const BASE = "/api/admin/promotions";
export interface TermsPreview {
  generated: string[];
  effective: string[];
}

// The backend may return a bare array or wrap it ({items|campaigns|coupons|redemptions}),
// and Mongo ids may arrive as `_id` or `id`. Normalise both so pages stay simple.
function unwrapList(data: unknown, key: string): any[] {
  if (Array.isArray(data)) return data;
  const obj = (data ?? {}) as Record<string, unknown>;
  const inner = obj[key] ?? obj.items;
  return Array.isArray(inner) ? inner : [];
}

function withId<T extends { id: string }>(row: any): T {
  return { ...row, id: String(row.id ?? row._id) } as T;
}

export const promotionsService = {
  getWelcomeBonus: async (): Promise<WelcomeBonusState> => {
    const res = await apiClient.get(`${BASE}/welcome-bonus`, { headers: adminHeaders() });
    return res.data;
  },

  updateWelcomeBonus: async (body: WelcomeBonusConfig): Promise<WelcomeBonusState> => {
    const res = await apiClient.put(`${BASE}/welcome-bonus`, body, { headers: adminHeaders() });
    return res.data;
  },

  listCampaigns: async (): Promise<Campaign[]> => {
    const res = await apiClient.get(`${BASE}/campaigns`, { headers: adminHeaders() });
    return unwrapList(res.data, "campaigns").map((r) => withId<Campaign>(r));
  },

  createCampaign: async (body: CampaignPayload): Promise<Campaign> => {
    const res = await apiClient.post(`${BASE}/campaigns`, body, { headers: adminHeaders() });
    return withId<Campaign>(res.data);
  },

  updateCampaign: async (id: string, body: CampaignPayload): Promise<Campaign> => {
    const res = await apiClient.put(`${BASE}/campaigns/${encodeURIComponent(id)}`, body, { headers: adminHeaders() });
    return withId<Campaign>(res.data);
  },

  deleteCampaign: async (id: string): Promise<void> => {
    await apiClient.delete(`${BASE}/campaigns/${encodeURIComponent(id)}`, { headers: adminHeaders() });
  },

  listCoupons: async (): Promise<Coupon[]> => {
    const res = await apiClient.get(`${BASE}/coupons`, { headers: adminHeaders() });
    return unwrapList(res.data, "coupons").map((r) => withId<Coupon>(r));
  },

  previewCouponTerms: async (body: CouponPayload): Promise<TermsPreview> => {
    const res = await apiClient.post(`${BASE}/coupons/terms-preview`, body, { headers: adminHeaders() });
    return res.data;
  },

  createCoupon: async (body: CouponPayload): Promise<Coupon> => {
    const res = await apiClient.post(`${BASE}/coupons`, body, { headers: adminHeaders() });
    return withId<Coupon>(res.data);
  },

  updateCoupon: async (id: string, body: CouponPayload): Promise<Coupon> => {
    const res = await apiClient.put(`${BASE}/coupons/${encodeURIComponent(id)}`, body, { headers: adminHeaders() });
    return withId<Coupon>(res.data);
  },

  setCouponActive: async (id: string, active: boolean): Promise<void> => {
    await apiClient.post(
      `${BASE}/coupons/${encodeURIComponent(id)}/${active ? "activate" : "deactivate"}`,
      {},
      { headers: adminHeaders() },
    );
  },

  listRedemptions: async (id: string, limit = 50, skip = 0): Promise<CouponRedemption[]> => {
    const res = await apiClient.get(`${BASE}/coupons/${encodeURIComponent(id)}/redemptions`, {
      params: { limit, skip },
      headers: adminHeaders(),
    });
    return unwrapList(res.data, "redemptions").map((r) => withId<CouponRedemption>(r));
  },

  // ── Existing-user campaigns ────────────────────────────────────────────────
  resolveUsers: async (identifiers: string[]): Promise<ResolveUsersResult> => {
    const res = await apiClient.post(`${BASE}/campaigns/resolve-users`, { identifiers }, { headers: adminHeaders() });
    return { matched: res.data?.matched ?? [], unmatched: res.data?.unmatched ?? [] };
  },

  audiencePreview: async (id: string): Promise<AudiencePreview> => {
    const res = await apiClient.post(`${BASE}/campaigns/${encodeURIComponent(id)}/audience-preview`, {}, { headers: adminHeaders() });
    return res.data;
  },

  applyCampaign: async (id: string, confirmTotalCredits: number): Promise<ApplyStartResult> => {
    const res = await apiClient.post(
      `${BASE}/campaigns/${encodeURIComponent(id)}/apply`,
      { confirm_total_credits: confirmTotalCredits },
      { headers: adminHeaders() },
    );
    return res.data;
  },

  campaignProgress: async (id: string): Promise<ApplyProgress> => {
    const res = await apiClient.get(`${BASE}/campaigns/${encodeURIComponent(id)}/progress`, { headers: adminHeaders() });
    return res.data;
  },

  listRecipients: async (id: string, f: RecipientsFilter, limit = 50, skip = 0): Promise<RecipientsPage> => {
    const res = await apiClient.get(`${BASE}/campaigns/${encodeURIComponent(id)}/recipients`, {
      params: { ...cleanFilter(f), limit, skip },
      headers: adminHeaders(),
    });
    return { items: res.data?.items ?? [], total: res.data?.total ?? 0 };
  },

  recipientsSummary: async (id: string): Promise<RecipientsSummary> => {
    const res = await apiClient.get(`${BASE}/campaigns/${encodeURIComponent(id)}/recipients/summary`, { headers: adminHeaders() });
    return res.data;
  },

  /** Downloads the CSV as a blob (the endpoint needs the admin auth header, which a plain link cannot send). */
  exportRecipients: async (id: string, f: RecipientsFilter, filename: string): Promise<void> => {
    const res = await apiClient.get(`${BASE}/campaigns/${encodeURIComponent(id)}/recipients/export`, {
      params: cleanFilter(f),
      headers: adminHeaders(),
      responseType: "blob",
    });
    const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  },
};

function cleanFilter(f: RecipientsFilter): Record<string, string> {
  const out: Record<string, string> = {};
  (Object.entries(f) as [string, string | undefined][]).forEach(([k, v]) => {
    if (v && v.trim()) out[k] = v.trim();
  });
  return out;
}
